import { useEffect, useState } from 'react';
import { Badge, Tag, Tooltip } from 'antd';

const LOCKED_MACHINE_PREVIEW_COUNT = 4;
const LOCKED_MACHINE_SCROLL_MAX_PX = 200;

export function parseMachineIdsList(ids, idsCsv, singleId) {
  if (Array.isArray(ids) && ids.length) return ids.map(Number).filter(Boolean);
  if (idsCsv) return String(idsCsv).split(',').map(Number).filter(Boolean);
  return singleId ? [Number(singleId)].filter(Boolean) : [];
}

export function machineIdArraysEqual(a, b) {
  const left = (Array.isArray(a) ? a : parseMachineIdsList(a)).slice().sort((x, y) => x - y);
  const right = (Array.isArray(b) ? b : parseMachineIdsList(b)).slice().sort((x, y) => x - y);
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
}

export function normalizeMovementKey(movementType) {
  return String(movementType || '').trim().toLowerCase();
}

export function buildCascaderLeafValue(machineIds, groupMachineIds) {
  const ids = machineIds || [];
  const allSelected = groupMachineIds.length > 0
    && ids.length === groupMachineIds.length
    && groupMachineIds.every((id) => ids.includes(id));
  if (allSelected) return '__all__';
  if (ids.length >= 1) return ids[0];
  return groupMachineIds[0] ?? '__all__';
}

export function inferTransferAnchor(fromIds, toIds, setupIds) {
  if (!setupIds?.length) return null;
  const from = parseMachineIdsList(fromIds);
  const to = parseMachineIdsList(toIds);
  const setup = parseMachineIdsList(setupIds);
  const fromIsSetup = machineIdArraysEqual(from, setup);
  const toIsSetup = machineIdArraysEqual(to, setup);
  if (toIsSetup && from.length && !fromIsSetup) return 'from';
  if (fromIsSetup && to.length && !toIsSetup) return 'to';
  return null;
}

export function resolveMachineDetails(machineIds, machineTypeWithGroups, setupEntry = {}) {
  const ids = parseMachineIdsList(machineIds);
  const catId = setupEntry.machine_type_id;
  const grpId = setupEntry.machine_group_id;
  const cat = (machineTypeWithGroups || []).find(
    (c) => String(c.machine_category_id) === String(catId)
  );
  const grp = cat?.machine_groups?.find(
    (g) => String(g.machine_group_id) === String(grpId)
  );
  const groupLabel = setupEntry.machine_type
    || (cat && grp ? `${cat.machine_category_name} / ${grp.machine_group_name}` : '');

  const machines = ids.map((id, index) => {
    const m = grp?.machines?.find((x) => String(x.machine_id) === String(id));
    const code = m?.machine_code || '';
    const label = m
      ? (m.machine_code ? `${m.machine_name} (${m.machine_code})` : m.machine_name)
      : `ID:${id}`;
    // Chip rows show `shortLabel` (code, falling back to the full label) —
    // stays compact instead of every chip carrying the full "Name (Code)".
    // The full-detail per-row list (LockedMachineRow) still uses `label`.
    const shortLabel = code || label;
    return {
      id,
      index: index + 1,
      label,
      shortLabel,
      categoryId: catId,
      groupId: grpId,
      categoryLabel: cat?.machine_category_name || '',
      groupLabel: grp?.machine_group_name || '',
    };
  });

  return { machines, groupLabel, catId, grpId };
}

function LockedMachineRow({ machine, isLast }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        borderBottom: isLast ? 'none' : '1px solid #f0f0f0',
        background: '#fff',
      }}
    >
      <span style={{ fontSize: 12, fontWeight: 700, color: '#204448', minWidth: 22 }}>
        {machine.index}.
      </span>
      <span
        style={{ fontSize: 13, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis' }}
        title={machine.tooltipText || machine.label}
      >
        {machine.label}
      </span>
    </div>
  );
}

export function LockedMachineSetupPanel({ machines, groupLabel, compact = false }) {
  const [expanded, setExpanded] = useState(false);
  const count = machines?.length || 0;
  const collapsible = count > LOCKED_MACHINE_PREVIEW_COUNT;

  const machineKey = machines.map((m) => m.id).join(',');
  useEffect(() => {
    setExpanded(false);
  }, [count, machineKey]);

  if (!count) {
    return (
      <div className="text-xs text-gray-400 dark:text-slate-400 py-2">
        No machines configured in Machine Setup.
      </div>
    );
  }

  if (compact) {
    const previewMachines = machines.slice(0, 3);
    const hiddenCount = count - previewMachines.length;
    return (
      <div>
        <div
          style={{
            width: '100%',
            minHeight: 48,
            border: '1px solid #d9d9d9',
            borderRadius: 6,
            background: '#fff',
            padding: '0 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
          }}
          title={groupLabel || ''}
        >
          <span
            style={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 13,
              color: '#111827',
              fontWeight: 500,
            }}
          >
            {groupLabel || 'Machines from setup'}
          </span>
          <span
            style={{
              flexShrink: 0,
              fontSize: 11,
              fontWeight: 600,
              color: '#204448',
              background: '#e0f2f1',
              padding: '2px 10px',
              borderRadius: 999,
            }}
            title={`${count} machine${count > 1 ? 's' : ''} selected`}
          >
            {count} selected
          </span>
        </div>
        <div className="flex items-center flex-wrap gap-1 mt-1.5">
          <Badge
            count={count}
            color="#204448"
            title={`${count} machine${count > 1 ? 's' : ''} selected`}
          />
          {previewMachines.map((machine) => (
            <Tooltip key={machine.id} title={machine.tooltipTitle || machine.tooltipText || machine.label}>
              <Tag className="text-[10px] leading-4 m-0">
                {machine.shortLabel}
              </Tag>
            </Tooltip>
          ))}
          {hiddenCount > 0 && (
            <Tooltip title={machines.map((machine) => machine.tooltipText || machine.label).join(', ')}>
              <Tag className="text-[10px] leading-4 m-0">
                +{hiddenCount} more
              </Tag>
            </Tooltip>
          )}
        </div>
      </div>
    );
  }

  const visibleMachines = collapsible && !expanded
    ? machines.slice(0, LOCKED_MACHINE_PREVIEW_COUNT)
    : machines;
  const hiddenCount = count - LOCKED_MACHINE_PREVIEW_COUNT;
  const useInnerScroll = collapsible && expanded;

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden', background: '#fafafa' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '8px 12px',
          borderBottom: '1px solid #f0f0f0',
          fontSize: 12,
          fontWeight: 600,
          color: '#374151',
          background: '#f3f4f6',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={groupLabel || ''}>
          {groupLabel || 'Machines from setup'}
        </span>
        <span
          style={{
            flexShrink: 0,
            fontSize: 11,
            fontWeight: 600,
            color: '#204448',
            background: '#e0f2f1',
            padding: '2px 8px',
            borderRadius: 10,
          }}
        >
          {count} selected
        </span>
      </div>
      <div
        style={{
          maxHeight: useInnerScroll ? LOCKED_MACHINE_SCROLL_MAX_PX : undefined,
          overflowY: useInnerScroll ? 'auto' : undefined,
        }}
      >
        {visibleMachines.map((m, idx) => (
          <LockedMachineRow
            key={m.id}
            machine={m}
            isLast={idx === visibleMachines.length - 1}
          />
        ))}
      </div>
      {collapsible ? (
        <div style={{ padding: '6px 12px', borderTop: '1px solid #f0f0f0', background: '#fafafa' }}>
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            style={{
              border: 'none',
              background: 'transparent',
              padding: 0,
              fontSize: 12,
              fontWeight: 600,
              color: '#0d9488',
              cursor: 'pointer',
            }}
          >
            {expanded
              ? 'Show less'
              : `Show all ${count} machines (+${hiddenCount} more)`}
          </button>
        </div>
      ) : null}
    </div>
  );
}
